// src/pages/MediaSummaryPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Loader2, AlertCircle, ZoomIn, ZoomOut, Bookmark, ChevronRight, ChevronsRight, ChevronsLeft } from 'lucide-react';
import apiClient from '../api/apiClient';
import { sanitizeHtml } from '../utils/sanitizeHtml';

// ----------------------------------------------------------------------
// توابع تبدیل HTML به بلوک و صفحه‌بندی (دقیقاً مشابه ProductSummaryPage)
// ----------------------------------------------------------------------
const splitHtmlToBlocks = (html) => {
  if (!html) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;
  const blocks = [];
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) blocks.push(`<span>${text}</span>`);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      const isBlock = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre'].includes(tagName);
      if (isBlock) {
        blocks.push(node.outerHTML);
      } else {
        blocks.push(node.outerHTML);
        node.childNodes.forEach(child => processNode(child));
      }
    }
  };
  body.childNodes.forEach(child => processNode(child));
  return blocks;
};

const countWordsInHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const text = tmp.textContent || tmp.innerText || '';
  return text.split(/\s+/).filter(w => w.length > 0).length;
};

const groupBlocksIntoPages = (blocks, wordsPerPage = 300) => {
  const pages = [];
  let currentPageBlocks = [];
  let currentWordCount = 0;
  for (const block of blocks) {
    const blockWordCount = countWordsInHtml(block);
    if (currentWordCount + blockWordCount > wordsPerPage && currentPageBlocks.length > 0) {
      pages.push(currentPageBlocks.join('\n'));
      currentPageBlocks = [];
      currentWordCount = 0;
    }
    currentPageBlocks.push(block);
    currentWordCount += blockWordCount;
  }
  if (currentPageBlocks.length > 0) pages.push(currentPageBlocks.join('\n'));
  return pages.length ? pages : ['<p>خلاصه‌ای وجود ندارد.</p>'];
};

// ----------------------------------------------------------------------
// کامپوننت صفحه‌بندی
// ----------------------------------------------------------------------
const PaginationControl = ({ currentPage, totalPages, onPageChange }) => {
  const [inputPage, setInputPage] = useState(currentPage + 1);
  useEffect(() => {
    setInputPage(currentPage + 1);
  }, [currentPage]);
  const handleGoToPage = () => {
    let page = parseInt(inputPage);
    if (isNaN(page)) page = 1;
    page = Math.max(1, Math.min(page, totalPages));
    onPageChange(page - 1);
    setInputPage(page);
  };
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 my-4 bg-green-50/60 p-2 rounded-lg shadow-sm border border-green-100">
      <button onClick={() => onPageChange(0)} disabled={currentPage === 0} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronsRight size={20} /></button>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronRight size={20} /></button>
      <span className="text-sm text-gray-700">صفحه {currentPage + 1} از {totalPages}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages - 1} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronLeft size={20} /></button>
      <button onClick={() => onPageChange(totalPages - 1)} disabled={currentPage === totalPages - 1} className="p-1 rounded hover:bg-green-100 disabled:opacity-40"><ChevronsLeft size={20} /></button>
      <div className="flex items-center gap-1 mr-4">
        <input type="number" value={inputPage} onChange={(e) => setInputPage(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleGoToPage()} className="w-16 text-center border border-green-200 rounded px-2 py-1 text-sm bg-white" min={1} max={totalPages} />
        <button onClick={handleGoToPage} className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700">برو</button>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// کامپوننت اصلی MediaSummaryPage
// ----------------------------------------------------------------------
const MediaSummaryPage = () => {
  const { mediaId } = useParams();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('mediaSummaryFontSize');
    return saved ? parseInt(saved) : 16;
  });
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [bookmark, setBookmark] = useState(null);

  useEffect(() => {
    const fetchMedia = async () => {
      if (!mediaId) return;
      setLoading(true);
      try {
        const response = await apiClient.get(`/products/media-summary/${mediaId}/`);
        const data = response.data;
        setMedia(data);
        const blocks = splitHtmlToBlocks(data.summary || '');
        const htmlPages = groupBlocksIntoPages(blocks, 300);
        setPages(htmlPages);
        setTotalPages(htmlPages.length);
        const savedPage = localStorage.getItem(`media_summary_page_${mediaId}`);
        setCurrentPage(savedPage ? parseInt(savedPage) : 0);
      } catch (err) {
        setError('امکان بارگذاری خلاصه وجود ندارد.');
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, [mediaId]);

  useEffect(() => {
    if (mediaId) localStorage.setItem(`media_summary_page_${mediaId}`, currentPage);
  }, [currentPage, mediaId]);

  useEffect(() => {
    if (!mediaId) return;
    const savedBookmark = localStorage.getItem(`media_bookmark_${mediaId}`);
    if (savedBookmark !== null) setBookmark(parseInt(savedBookmark));
  }, [mediaId]);

  useEffect(() => {
    if (mediaId && bookmark !== null) localStorage.setItem(`media_bookmark_${mediaId}`, bookmark);
    else if (mediaId && bookmark === null) localStorage.removeItem(`media_bookmark_${mediaId}`);
  }, [bookmark, mediaId]);

  const increaseFont = () => {
    if (fontSize < 28) {
      const newSize = fontSize + 2;
      setFontSize(newSize);
      localStorage.setItem('mediaSummaryFontSize', newSize);
    }
  };
  const decreaseFont = () => {
    if (fontSize > 12) {
      const newSize = fontSize - 2;
      setFontSize(newSize);
      localStorage.setItem('mediaSummaryFontSize', newSize);
    }
  };

  const setBookmarkHere = () => {
    setBookmark(currentPage);
    alert(`بوکمارک در صفحه ${currentPage + 1} ثبت شد.`);
  };
  const goToBookmark = () => {
    if (bookmark !== null && bookmark >= 0 && bookmark < totalPages) {
      setCurrentPage(bookmark);
    } else {
      alert('بوکمارکی تنظیم نشده است.');
    }
  };
  const clearBookmark = () => {
    setBookmark(null);
    alert('بوکمارک حذف شد.');
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen bg-gradient-to-b from-green-50 to-stone-100" style={{ fontFamily: "'Vazirmatn', 'Tahoma', sans-serif" }}><Loader2 size={40} className="animate-spin text-green-600" /></div>;
  if (error || !media) return <div className="text-center text-red-600 p-4" style={{ fontFamily: "'Vazirmatn', 'Tahoma', sans-serif" }}>{error || 'خلاصه‌ای یافت نشد.'}</div>;

  const hasAccess = media?.summary !== null && media?.summary !== '';
  const isFree = media?.product_is_free === true;
  const productSlug = media?.product_slug || `product-${media?.product}`;

  if (!hasAccess && !isFree) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-stone-100 rtl" dir="rtl" style={{ fontFamily: "'Vazirmatn', 'Tahoma', sans-serif" }}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="bg-green-50/90 rounded-xl shadow-md p-8 text-center">
            <AlertCircle size={48} className="text-green-600 mx-auto mb-4" />
            <p className="text-gray-700 mb-4">برای مشاهده چکیده این بخش، ابتدا محصول را خریداری کنید.</p>
            <Link to={`/products/${productSlug}`} className="inline-block px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">بازگشت به صفحه محتوا</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-green-50 to-stone-100 rtl"
      dir="rtl"
      style={{ fontFamily: "'Vazirmatn', 'Tahoma', sans-serif" }}
    >
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="mb-4 text-sm text-gray-600 flex items-center flex-wrap gap-1">
          <Link to="/" className="hover:text-green-700">خانه</Link>
          <ChevronLeft size={14} className="mx-1" />
          <Link to={`/products/${productSlug}`} className="hover:text-green-700">{media.product_title || 'محصول'}</Link>
          <ChevronLeft size={14} className="mx-1" />
          <span className="text-gray-800 font-medium">چکیده فایل</span>
        </div>

        {/* Header */}
        <div className="bg-green-50/80 rounded-xl shadow-md overflow-hidden mb-6 border border-green-100">
          <div className="p-4">
            <h1 className="text-lg md:text-xl font-bold text-gray-800 text-right">چکیده فایل</h1>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 bg-green-50/60 p-3 rounded-lg shadow-sm border border-green-100">
          <div className="flex items-center gap-2">
            <button onClick={decreaseFont} disabled={fontSize <= 12} className="p-1 rounded-full hover:bg-green-100 disabled:opacity-40"><ZoomOut size={20} /></button>
            <span className="text-xs text-gray-700">{fontSize}px</span>
            <button onClick={increaseFont} disabled={fontSize >= 28} className="p-1 rounded-full hover:bg-green-100 disabled:opacity-40"><ZoomIn size={20} /></button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={setBookmarkHere} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-md hover:bg-green-200 text-sm"><Bookmark size={16} /> نشانه‌گذاری این صفحه</button>
            {bookmark !== null && (
              <>
                <button onClick={goToBookmark} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-md hover:bg-green-200 text-sm"><Bookmark size={16} /> رفتن به نشانه (صفحه {bookmark+1})</button>
                <button onClick={clearBookmark} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-md hover:bg-purple-200 text-sm">حذف نشانه</button>
              </>
            )}
          </div>
        </div>

        {totalPages > 1 && <PaginationControl currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}

        <div className="bg-green-50/90 rounded-xl shadow-md p-6 border border-green-100">
          <div
            className="prose prose-lg max-w-none text-justify prose-headings:font-bold prose-a:text-indigo-600 prose-strong:text-gray-800 prose-ul:list-disc prose-ol:list-decimal rtl"
            style={{
              fontSize: `${fontSize}px`,
              fontFamily: "'Vazirmatn', 'Tahoma', sans-serif",
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(pages[currentPage] || '<p>خلاصه‌ای وجود ندارد.</p>') }}
          />
        </div>

        {totalPages > 1 && <PaginationControl currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}

        <div className="mt-6 text-center">
          <Link to={`/products/${productSlug}`} className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-green-800 bg-green-100 hover:bg-green-200 transition">بازگشت به صفحه محتوا</Link>
        </div>
      </div>
    </div>
  );
};

export default MediaSummaryPage;